import { databaseConfig } from '../../config/database';
import type {
  CupsPrinterInfo,
  ReceiptPrinterConfig,
  ThermalReceiptData,
} from '../../printing/thermalReceiptTypes';
import { RECEIPT_PRINTER_NOT_CONFIGURED } from '../../printing/thermalReceiptTypes';

const API = databaseConfig.mongodb.apiBaseUrl.replace(/\/$/, '');

export type ReceiptPrintApiResult =
  | { ok: true; printer: string }
  | { ok: false; code: string; error: string };

async function apiFetch<T>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<{ ok: boolean; status: number; json: T | null }> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  let json: T | null = null;
  try {
    json = (await res.json()) as T;
  } catch {
    json = null;
  }
  return { ok: res.ok, status: res.status, json };
}

export async function fetchReceiptPrinterConfig(
  token: string,
): Promise<ReceiptPrinterConfig | null> {
  const res = await apiFetch<{ data: ReceiptPrinterConfig | null }>(
    '/api/kiosk/receipt-printer/config',
    token,
  );
  if (!res.ok) return null;
  return res.json?.data ?? null;
}

export async function fetchAvailableReceiptPrinters(
  token: string,
): Promise<CupsPrinterInfo[]> {
  const res = await apiFetch<{ data: CupsPrinterInfo[] }>(
    '/api/kiosk/receipt-printer/printers',
    token,
  );
  if (!res.ok) return [];
  return res.json?.data ?? [];
}

export async function saveReceiptPrinterConfigApi(
  token: string,
  printerName: string,
): Promise<ReceiptPrinterConfig | null> {
  const res = await apiFetch<{ data: ReceiptPrinterConfig }>(
    '/api/kiosk/receipt-printer/config',
    token,
    {
      method: 'PUT',
      body: JSON.stringify({ printerName }),
    },
  );
  if (!res.ok) return null;
  return res.json?.data ?? null;
}

export async function printReceiptViaApi(
  token: string,
  data: ThermalReceiptData,
  printerName?: string,
  paperSize?: string,
): Promise<ReceiptPrintApiResult> {
  const body: Record<string, unknown> = { ...data };
  if (printerName?.trim()) body.printerName = printerName.trim();
  if (paperSize === '58mm' || paperSize === '80mm') {
    body.paperSize = paperSize;
  }
  const res = await apiFetch<{ data?: { printer: string }; code?: string; message?: string | { code?: string } }>(
    '/api/kiosk/receipt-printer/print',
    token,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  if (res.ok && res.json?.data?.printer) {
    return { ok: true, printer: res.json.data.printer };
  }

  const payload = res.json as {
    code?: string;
    message?: string | { code?: string; message?: string };
  } | null;

  const nested =
    typeof payload?.message === 'object' && payload.message !== null
      ? payload.message
      : null;

  const code =
    payload?.code ??
    nested?.code ??
    (typeof payload?.message === 'string' ? payload.message : undefined) ??
    RECEIPT_PRINTER_NOT_CONFIGURED;

  return {
    ok: false,
    code,
    error:
      nested?.message ??
      (typeof payload?.message === 'string' ? payload.message : code),
  };
}
